if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.submitButtonSpinner = this.submitButton.querySelector('.loading__spinner');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.bindLinePropertyLists();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.updateLinePropertyLists();
        if (!this.validatePersonalizationFields()) return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.submitButtonSpinner) this.submitButtonSpinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.appendAssociatedLineProperties(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal && typeof quickAddModal.hide === 'function') {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // Fallback: if quick-add-modal API isn't available, still paint cart updates immediately.
              if (quickAddModal) {
                quickAddModal.removeAttribute('open');
              }
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (this.submitButtonSpinner) this.submitButtonSpinner.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      appendAssociatedLineProperties(formData) {
        if (!this.form.id) return;

        const fields = Array.from(
          document.querySelectorAll(`[form="${this.form.id}"][name^="properties["]`)
        ).filter((field) => !this.form.contains(field) && !field.disabled);
        const fieldNames = [...new Set(fields.map((field) => field.name))];

        fieldNames.forEach((fieldName) => {
          const group = fields.filter((field) => field.name === fieldName);
          const values = group
            .filter((field) => {
              if (field.type === 'checkbox' || field.type === 'radio') return field.checked;
              return field.value.trim() !== '';
            })
            .map((field) => field.value);

          formData.delete(fieldName);
          values.forEach((value) => formData.append(fieldName, value));
        });

        const propertyListFields = Array.from(
          document.querySelectorAll(`[form="${this.form.id}"][data-product-line-property-list]`)
        ).filter((field) => !field.disabled);
        const propertyListGroupIds = [
          ...new Set(propertyListFields.map((field) => field.dataset.productLinePropertyList)),
        ];

        propertyListGroupIds.forEach((groupId) => {
          const hiddenField = document.querySelector(
            `[form="${this.form.id}"][data-product-line-property-hidden="${groupId}"]`
          );
          if (!hiddenField || !hiddenField.name) return;

          const value = propertyListFields
            .filter((field) => field.dataset.productLinePropertyList === groupId && field.checked)
            .map((field) => field.value)
            .join(', ');

          formData.delete(hiddenField.name);
          if (value) formData.append(hiddenField.name, value);
        });
      }

      bindLinePropertyLists() {
        if (!this.form.id) return;

        const fields = document.querySelectorAll(`[form="${this.form.id}"][data-product-line-property-list]`);
        fields.forEach((field) => {
          field.addEventListener('change', () => this.updateLinePropertyLists());
        });
        this.updateLinePropertyLists();
      }

      updateLinePropertyLists() {
        if (!this.form.id) return;

        const fields = Array.from(
          document.querySelectorAll(`[form="${this.form.id}"][data-product-line-property-list]`)
        );
        const groupIds = [...new Set(fields.map((field) => field.dataset.productLinePropertyList))];

        groupIds.forEach((groupId) => {
          const hiddenField = document.querySelector(
            `[form="${this.form.id}"][data-product-line-property-hidden="${groupId}"]`
          );
          if (!hiddenField) return;

          hiddenField.value = fields
            .filter((field) => field.dataset.productLinePropertyList === groupId && field.checked)
            .map((field) => field.value)
            .join(', ');
        });
      }

      validatePersonalizationFields() {
        if (!this.form.id) return true;

        const fields = document.querySelectorAll(
          `[form="${this.form.id}"][data-product-personalization-required="true"], [form="${this.form.id}"][data-product-line-property-required="true"]`
        );
        const checkedNames = [];

        for (const field of fields) {
          const fieldGroup = field.dataset.productPropertyGroup || field.name;
          if (checkedNames.includes(fieldGroup)) continue;

          const group = Array.from(fields).filter(
            (candidate) => (candidate.dataset.productPropertyGroup || candidate.name) === fieldGroup
          );
          const isChoiceField = field.type === 'checkbox' || field.type === 'radio';
          const isValid = isChoiceField
            ? group.some((candidate) => candidate.checked)
            : field.value.trim() !== '';

          if (isValid) {
            checkedNames.push(fieldGroup);
            group.forEach((candidate) => candidate.removeAttribute('aria-invalid'));
            continue;
          }

          group.forEach((candidate) => candidate.setAttribute('aria-invalid', 'true'));
          this.handleErrorMessage(field.dataset.requiredMessage || field.validationMessage);
          field.focus();
          group.forEach((candidate) => {
            candidate.addEventListener(
              isChoiceField ? 'change' : 'input',
              () => {
                const isNowValid = isChoiceField
                  ? group.some((groupCandidate) => groupCandidate.checked)
                  : field.value.trim() !== '';

                if (isNowValid) {
                  group.forEach((groupCandidate) => groupCandidate.removeAttribute('aria-invalid'));
                  this.handleErrorMessage();
                }
              },
              { once: true }
            );
          });

          return false;
        }

        return true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
